/**
 * Country code phone validation utility.
 * Provides per-country phone number format validation with regex patterns,
 * display names, dial codes, placeholders, and digit length constraints.
 */

export const countryCodes = [
  { code: 'US', dialCode: '+1',  name: 'United States',       pattern: /^\(\d{3}\)\s?\d{3}-\d{4}$|^\d{10}$/, placeholder: '(555) 000-0000', minDigits: 10, maxDigits: 10 },
  { code: 'CA', dialCode: '+1',  name: 'Canada',              pattern: /^\(\d{3}\)\s?\d{3}-\d{4}$|^\d{10}$/, placeholder: '(555) 000-0000', minDigits: 10, maxDigits: 10 },
  { code: 'GB', dialCode: '+44', name: 'United Kingdom',      pattern: /^\d{10,11}$/, placeholder: '7911 123456', minDigits: 10, maxDigits: 11 },
  { code: 'DE', dialCode: '+49', name: 'Germany',             pattern: /^\d{10,12}$/, placeholder: '151 12345678', minDigits: 10, maxDigits: 12 },
  { code: 'FR', dialCode: '+33', name: 'France',              pattern: /^\d{9,10}$/, placeholder: '6 12 34 56 78', minDigits: 9, maxDigits: 10 },
  { code: 'IN', dialCode: '+91', name: 'India',               pattern: /^\d{10}$/, placeholder: '91234 56789', minDigits: 10, maxDigits: 10 },
  { code: 'AU', dialCode: '+61', name: 'Australia',           pattern: /^\d{9,10}$/, placeholder: '412 345 678', minDigits: 9, maxDigits: 10 },
  { code: 'JP', dialCode: '+81', name: 'Japan',               pattern: /^\d{10,11}$/, placeholder: '90 1234 5678', minDigits: 10, maxDigits: 11 },
  { code: 'CN', dialCode: '+86', name: 'China',               pattern: /^\d{11}$/, placeholder: '131 2345 6789', minDigits: 11, maxDigits: 11 },
  { code: 'BR', dialCode: '+55', name: 'Brazil',              pattern: /^\d{10,11}$/, placeholder: '11 91234 5678', minDigits: 10, maxDigits: 11 },
  { code: 'MX', dialCode: '+52', name: 'Mexico',              pattern: /^\d{10}$/, placeholder: '55 1234 5678', minDigits: 10, maxDigits: 10 },
  { code: 'KR', dialCode: '+82', name: 'South Korea',         pattern: /^\d{9,11}$/, placeholder: '10 1234 5678', minDigits: 9, maxDigits: 11 },
  { code: 'IT', dialCode: '+39', name: 'Italy',               pattern: /^\d{9,11}$/, placeholder: '312 345 6789', minDigits: 9, maxDigits: 11 },
  { code: 'ES', dialCode: '+34', name: 'Spain',               pattern: /^\d{9}$/, placeholder: '612 345 678', minDigits: 9, maxDigits: 9 },
  { code: 'NL', dialCode: '+31', name: 'Netherlands',         pattern: /^\d{9,10}$/, placeholder: '6 12345678', minDigits: 9, maxDigits: 10 },
  { code: 'SE', dialCode: '+46', name: 'Sweden',              pattern: /^\d{7,10}$/, placeholder: '70 123 45 67', minDigits: 7, maxDigits: 10 },
  { code: 'CH', dialCode: '+41', name: 'Switzerland',         pattern: /^\d{9,10}$/, placeholder: '78 123 45 67', minDigits: 9, maxDigits: 10 },
  { code: 'SG', dialCode: '+65', name: 'Singapore',           pattern: /^\d{8}$/, placeholder: '9123 4567', minDigits: 8, maxDigits: 8 },
  { code: 'AE', dialCode: '+971',name: 'United Arab Emirates', pattern: /^\d{7,9}$/, placeholder: '50 123 4567', minDigits: 7, maxDigits: 9 },
  { code: 'SA', dialCode: '+966',name: 'Saudi Arabia',        pattern: /^\d{8,9}$/, placeholder: '51 234 5678', minDigits: 8, maxDigits: 9 },
  { code: 'ZA', dialCode: '+27', name: 'South Africa',        pattern: /^\d{9}$/, placeholder: '71 123 4567', minDigits: 9, maxDigits: 9 },
  { code: 'NG', dialCode: '+234',name: 'Nigeria',             pattern: /^\d{10,11}$/, placeholder: '802 123 4567', minDigits: 10, maxDigits: 11 },
  { code: 'KE', dialCode: '+254',name: 'Kenya',               pattern: /^\d{9,10}$/, placeholder: '712 345678', minDigits: 9, maxDigits: 10 },
  { code: 'IL', dialCode: '+972',name: 'Israel',              pattern: /^\d{9,10}$/, placeholder: '50 123 4567', minDigits: 9, maxDigits: 10 },
  { code: 'PH', dialCode: '+63', name: 'Philippines',         pattern: /^\d{10}$/, placeholder: '917 123 4567', minDigits: 10, maxDigits: 10 },
  { code: 'MY', dialCode: '+60', name: 'Malaysia',            pattern: /^\d{9,10}$/, placeholder: '12 345 6789', minDigits: 9, maxDigits: 10 },
  { code: 'ID', dialCode: '+62', name: 'Indonesia',           pattern: /^\d{9,13}$/, placeholder: '812 345 6789', minDigits: 9, maxDigits: 13 },
  { code: 'PK', dialCode: '+92', name: 'Pakistan',            pattern: /^\d{10}$/, placeholder: '301 2345678', minDigits: 10, maxDigits: 10 },
  { code: 'BD', dialCode: '+880',name: 'Bangladesh',          pattern: /^\d{10}$/, placeholder: '1712 345678', minDigits: 10, maxDigits: 10 },
  { code: 'RU', dialCode: '+7',  name: 'Russia',              pattern: /^\d{10}$/, placeholder: '912 345 6789', minDigits: 10, maxDigits: 10 },
];

/**
 * Strips non-digit characters from a phone number string.
 */
export const stripNonDigits = (phone) => phone.replace(/\D/g, '');

/**
 * Gets a country entry by its country code (e.g. 'US', 'IN').
 */
export const getCountryByCode = (code) => {
  return countryCodes.find(c => c.code === code) || countryCodes[0];
};

/**
 * Gets a country entry by its dial code (e.g. '+1', '+91').
 */
export const getCountryByDialCode = (dialCode) => {
  return countryCodes.find(c => c.dialCode === dialCode);
};

/**
 * Validates a phone number for a given country code.
 * @param {string} phone - The phone number (digits only or with formatting)
 * @param {string} countryCode - The ISO country code (e.g. 'US', 'IN')
 * @returns {{ valid: boolean, message: string }}
 */
export const validatePhoneForCountry = (phone, countryCode = 'US') => {
  if (!phone || phone.trim() === '') {
    return { valid: false, message: 'Phone number is required' };
  }

  const country = getCountryByCode(countryCode);
  if (!country) {
    return { valid: false, message: 'Invalid country code' };
  }

  // Strip the dial code prefix if present before counting digits
  let localPhone = phone.trim();
  if (localPhone.startsWith(country.dialCode)) {
    localPhone = localPhone.slice(country.dialCode.length).trim();
  }

  const digits = stripNonDigits(localPhone);

  if (digits.length < country.minDigits) {
    return { valid: false, message: `Phone number must be at least ${country.minDigits} digits for ${country.name}` };
  }

  if (digits.length > country.maxDigits) {
    return { valid: false, message: `Phone number must be at most ${country.maxDigits} digits for ${country.name}` };
  }

  return { valid: true, message: '' };
};

/**
 * Formats a full phone number with country dial code prefix.
 * @param {string} dialCode - e.g. '+1'
 * @param {string} phone - The local part of the phone number
 * @returns {string}
 */
export const formatFullPhone = (dialCode, phone) => {
  if (!phone) return '';
  return `${dialCode} ${phone}`;
};

/**
 * Parses a full phone string (e.g., "+91 9876543210") into
 * { countryCode, dialCode, localNumber }.
 * Tries to match the longest dial code first.
 */
export const parsePhoneWithCountryCode = (fullPhone) => {
  if (!fullPhone) return { countryCode: 'US', dialCode: '+1', localNumber: '' };

  const cleaned = fullPhone.trim();

  if (cleaned.startsWith('+')) {
    // Sort by dial code length descending to match longest first
    const sorted = [...countryCodes].sort((a, b) => b.dialCode.length - a.dialCode.length);
    for (const country of sorted) {
      if (cleaned.startsWith(country.dialCode)) {
        const localNumber = cleaned.slice(country.dialCode.length).trim();
        return { countryCode: country.code, dialCode: country.dialCode, localNumber };
      }
    }
  }

  // No match — return as-is with default US
  return { countryCode: 'US', dialCode: '+1', localNumber: cleaned };
};
